import { useEffect, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MapPin, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import TrustBadges from "./TrustBadges";
import ZooplaBranchButton from "./ZooplaBranchButton";
import api from "../lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_34823b17-4df1-4fcc-aabf-6685e72203af/artifacts/pr01brpt_48c454c2-879b-4669-8888-6550c01d8b31.jpeg";
const PHONE_NUMBER = "01924 520163";
const PHONE_LINK = "tel:01924520163";
const DEFAULT_FOOTER_ADDRESS = "Keystone Properties, 17 Commercial Street, Batley, WF17 5HJ";

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [footerAddress, setFooterAddress] = useState(DEFAULT_FOOTER_ADDRESS);
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    api
      .getContentSection("footer")
      .then((res) => {
        if (mounted && res?.content?.address) {
          setFooterAddress(res.content.address);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Properties", path: "/properties" },
    {
      name: "Services",
      children: [
        { name: "Property Management", path: "/services/property-management" },
        { name: "Management Packages", path: "/services/packages" },
        { name: "Additional Services", path: "/services/packages#additional-services" },
        { name: "Lettings", path: "/services/lettings" },
        { name: "Investments", path: "/services/investments" },
      ],
    },
    { name: "Landlords", path: "/landlords" },
    { name: "Tenants", path: "/tenants" },
    { name: "News", path: "/news" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Bar */}
      <div className="bg-navy text-white py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href={PHONE_LINK} className="flex items-center gap-2 hover:text-gold transition-colors">
              <Phone className="w-4 h-4" />
              <span>{PHONE_NUMBER}</span>
            </a>
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span data-testid="topbar-address">{footerAddress}</span>
            </span>
          </div>
          <div className="text-white/70">
            Serving Leeds, Bradford, Batley, Dewsbury & West Yorkshire
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center h-20 lg:h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 mr-8 lg:mr-12" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="Keystone Properties Logo" 
                className="w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12 object-contain flex-shrink-0"
              />
              <div className="flex flex-col justify-center leading-tight">
                <span className="text-navy font-heading text-lg sm:text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap">
                  Keystone Properties
                </span>
                <span className="text-navy/70 text-[10px] sm:text-xs font-medium tracking-wide hidden sm:block whitespace-nowrap">
                  Property Management | Lettings | Investments
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-0.5">
              {navLinks.map((link) =>
                link.children ? (
                  <DropdownMenu key={link.name}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="px-3 py-2 text-navy hover:text-navy-light text-sm font-medium flex items-center gap-1 transition-colors"
                        data-testid={`nav-${link.name.toLowerCase()}`}
                      >
                        {link.name}
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                      {link.children.map((child) => (
                        <DropdownMenuItem key={child.path} asChild>
                          <Link
                            to={child.path}
                            className="w-full cursor-pointer"
                            data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {child.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-3 py-2 text-sm font-medium transition-colors nav-link ${
                      isActive(link.path)
                        ? "text-gold"
                        : "text-navy hover:text-navy-light"
                    }`}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                )
              )}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <Button
                asChild
                className="bg-navy hover:bg-navy-light text-white btn-primary"
                data-testid="nav-cta-button"
              >
                <Link to="/contact">Book Valuation</Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-navy"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-slate-200 py-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-2">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.name} className="space-y-1">
                    <div className="px-4 py-2 font-medium text-navy">{link.name}</div>
                    {link.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className="block px-8 py-2 text-slate-600 hover:text-navy hover:bg-slate-50 rounded-md"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-4 py-2 font-medium rounded-md ${
                      isActive(link.path)
                        ? "text-gold bg-gold/5"
                        : "text-navy hover:bg-slate-50"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                )
              )}
              <div className="pt-4 px-4">
                <Button
                  asChild
                  className="w-full bg-navy hover:bg-navy-light text-white"
                >
                  <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                    Book Valuation
                  </Link>
                </Button>
              </div>
              <div className="pt-4 px-4 space-y-2 text-sm text-slate-600">
                <a href={PHONE_LINK} className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {PHONE_NUMBER}
                </a>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {footerAddress}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-navy text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div>
              <div className="mb-4">
                <span className="font-heading text-2xl font-bold text-white">Keystone</span>
                <span className="font-heading text-2xl font-bold text-gold ml-1">Properties</span>
                <p className="text-white/60 text-sm mt-1">Property Management | Lettings | Investments</p>
              </div>
              <p className="text-white/70 mb-6">
                Based in Batley, we help landlords in Leeds, Bradford, Batley, Dewsbury and surrounding areas maximise rental income and protect their investment—without the day-to-day stress.
              </p>
              <div className="space-y-2 text-sm">
                <a href={PHONE_LINK} className="flex items-center gap-2 hover:text-gold transition-colors">
                  <Phone className="w-4 h-4" />
                  {PHONE_NUMBER}
                </a>
                <div className="flex items-center gap-2 text-white/70">
                  <MapPin className="w-4 h-4" />
                  <span data-testid="footer-address">{footerAddress}</span>
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-heading font-semibold text-lg mb-6">Services</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/services/property-management" className="text-white/70 hover:text-gold transition-colors">
                    Property Management
                  </Link>
                </li>
                <li>
                  <Link to="/services/lettings" className="text-white/70 hover:text-gold transition-colors">
                    Lettings
                  </Link>
                </li>
                <li>
                  <Link to="/services/investments" className="text-white/70 hover:text-gold transition-colors">
                    Investments
                  </Link>
                </li>
                <li>
                  <Link to="/landlords" className="text-white/70 hover:text-gold transition-colors">
                    For Landlords
                  </Link>
                </li>
                <li>
                  <Link to="/tenants" className="text-white/70 hover:text-gold transition-colors">
                    For Tenants
                  </Link>
                </li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading font-semibold text-lg mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/properties" className="text-white/70 hover:text-gold transition-colors">
                    Available Properties
                  </Link>
                </li>
                <li>
                  <Link to="/news" className="text-white/70 hover:text-gold transition-colors">
                    News & Updates
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-white/70 hover:text-gold transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-white/70 hover:text-gold transition-colors">
                    Contact Us
                  </Link>
                </li>
              </ul>
              <div className="mt-6">
                <ZooplaBranchButton
                  variant="outline"
                  label="View us on Zoopla"
                  testId="zoopla-footer"
                />
              </div>
            </div>

            {/* Areas Served */}
            <div>
              <h4 className="font-heading font-semibold text-lg mb-6">Areas We Serve</h4>
              <ul className="space-y-3 text-white/70">
                <li>Batley</li>
                <li>Dewsbury</li>
                <li>Heckmondwike</li>
                <li>Huddersfield</li>
                <li>Leeds</li>
                <li>Bradford</li>
              </ul>
            </div>
          </div>

          {/* Trust Badges Section */}
          <TrustBadges variant="footer" />

          <div className="border-t border-white/10 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              © {new Date().getFullYear()} Keystone Properties. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-white/50">
              <Link to="/landlord-portal" className="hover:text-white transition-colors">
                Landlord Portal
              </Link>
              <Link to="/admin" className="hover:text-white transition-colors">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
